import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import {
  Trophy, Users, Heart, Activity, Sparkles, Target,
  ChevronDown, ArrowRight, Star
} from 'lucide-react';

// Import pages
import RecreationalPage from './pages/RecreationalPage';
import PreCompPage from './pages/PreCompPage';
import CompetitivePage from './pages/CompetitivePage';
import ElitePage from './pages/ElitePage';
import AfterSchoolPage from './pages/AfterSchoolPage';
import ContactPage from './pages/ContactPage';

// Shared layout components
import Header from './components/Header';
import Footer from './components/Footer';

function HomePage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', program: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const programs = [
    {
      title: 'Recreational Division',
      age: 'Ages 5-7',
      description: 'A fun introduction to the fundamentals. Perfect for young players discovering the joy of soccer through movement and basic skills.',
      icon: Sparkles,
      image: 'https://images.unsplash.com/photo-1604651684573-05470013b3b9?w=800&q=80',
      link: '/recreational'
    },
    {
      title: 'Pre-Comp Division',
      age: 'Ages 8-11',
      description: 'For players who desire serious development without a full competitive commitment. Focus on skills and small-sided games.',
      icon: Target,
      image: 'https://images.unsplash.com/photo-1629977007371-0ba395424741?w=800&q=80',
      link: '/precomp'
    },
    {
      title: 'Competitive Division',
      age: 'Ages 12-15',
      description: 'For kids ready for full commitment to competitive leagues. Emphasis on tactical awareness, fitness, and game intelligence.',
      icon: Trophy,
      image: 'https://images.unsplash.com/photo-1739550635585-484633b21450?w=800&q=80',
      link: '/competitive'
    },
    {
      title: 'Elite Division',
      age: 'Ages 16-18',
      description: 'For dedicated athletes ready to embrace the demands of high-level youth soccer. College-ready training and preparation.',
      icon: Activity,
      image: 'https://images.unsplash.com/photo-1527950285759-4d9f1b69c461?w=800&q=80',
      link: '/elite'
    }
  ];

  const values = [
    { icon: Heart, title: 'Character', description: 'We demonstrate integrity, respect, and responsibility in all actions.' },
    { icon: Activity, title: 'Resilience', description: 'We embrace challenges and learn from setbacks with mental toughness.' },
    { icon: Users, title: 'Sportsmanship', description: 'We honor the game, respect opponents, and celebrate teamwork.' },
    { icon: Trophy, title: 'Fitness', description: 'We commit to health, wellness, and peak physical conditioning.' },
    { icon: Sparkles, title: 'Community', description: 'We build supportive relationships that extend beyond the field.' },
    { icon: Target, title: 'Fun', description: 'We foster an enthusiastic, joyful environment for lifelong love of the game.' }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Parent of Recreational Player',
      content: 'Soccer Sphere has been amazing for my daughter! She looks forward to every practice and has made wonderful friends. The coaches are patient and really focus on making it fun while teaching fundamental skills.',
      rating: 5
    },
    {
      name: 'Marcus Johnson',
      role: 'Competitive Division Player',
      content: 'The competitive division pushed me to become a better player and teammate. The coaching is top-notch and the emphasis on character development has helped me both on and off the field.',
      rating: 5
    },
    {
      name: 'Jennifer Chen',
      role: 'Parent of Pre-Comp Player',
      content: 'We switched from another club and the difference is night and day. Soccer Sphere truly cares about each child\'s development and creates a positive, supportive environment.',
      rating: 5
    },
    {
      name: 'David Rodriguez',
      role: 'Elite Division Player',
      content: 'Thanks to Soccer Sphere\'s elite program, I earned a college scholarship! The training prepared me not just technically, but mentally for the next level. Forever grateful!',
      rating: 5
    }
  ];

  const faqs = [
    {
      question: 'What age groups do you serve?',
      answer: 'Soccer Sphere serves players from ages 5-18 across four divisions: Recreational (5-7), Pre-Comp (8-11), Competitive (12-15), and Elite (16-18). We have programs designed for every skill level and commitment level.'
    },
    {
      question: 'How do I know which division is right for my child?',
      answer: 'We offer free assessment sessions to help place your child in the right division. Our coaches evaluate skill level, experience, and commitment to recommend the best fit. You can also contact us for a consultation to discuss your child\'s goals.'
    },
    {
      question: 'What is the time commitment for each division?',
      answer: 'Recreational: 1-2 practices per week. Pre-Comp: 2-3 practices per week plus weekend games. Competitive: 3-4 practices per week plus league games and tournaments. Elite: 4-5 practices per week plus year-round competitive schedule.'
    },
    {
      question: 'Do you offer financial assistance?',
      answer: 'Yes! Soccer Sphere believes every child should have access to quality soccer training. We offer scholarships and payment plans based on need. Contact us to learn more about financial assistance options.'
    },
    {
      question: 'What makes Soccer Sphere different from other clubs?',
      answer: 'We focus on the whole player - not just technical skills, but character development, resilience, and life skills. Our comprehensive approach creates better players and better people. Plus, our experienced coaching staff ensures personalized attention for every player.'
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => {
      setFormSubmitted(false);
      setFormData({ name: '', email: '', program: '', message: '' });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 opacity-95"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1622659097509-4d56de14539e?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.25
        }}></div>
        
        {/* Animated Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-green-700/10 to-blue-900/10 animate-pulse"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-fade-in-up">
            New Era of Youth
            <span className="block bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent animate-gradient">
              Soccer Development
            </span>
          </h1>
          
          <p className="text-xl sm:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto animate-fade-in-up-delay">
            Empowering youth through soccer, building strong character, teaching resilience, 
            fostering sportsmanship, and cultivating community on and off the field.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up-delay-2">
            <a href="#contact" className="group bg-gradient-to-r from-green-700 to-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:opacity-90 transition flex items-center space-x-2">
              <span>Start Your Journey</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition" />
            </a>
            <a href="#programs" className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/20 transition">
              View Programs
            </a>
          </div>

          <div className="mt-12 text-sm text-gray-400">
            <p>Free assessment session • No commitment required • Ages 5-18</p>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4 animate-fade-in-up">
              Discover Your Perfect
              <span className="block bg-gradient-to-r from-green-700 to-green-600 bg-clip-text text-transparent animate-gradient">
                Development Path
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              From first kick to college recruitment, we have a program designed for every player's journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {programs.map((program, index) => {
              const Icon = program.icon;
              return (
                <Link 
                  key={index} 
                  to={program.link}
                  className="group bg-white rounded-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300 border border-gray-200 hover:border-green-600 hover:shadow-2xl"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img src={program.image} alt={program.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                    <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm p-3 rounded-full">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="text-green-700 text-sm font-semibold mb-2">{program.age}</div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{program.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{program.description}</p>
                    <button className="mt-4 text-green-700 hover:text-green-600 font-semibold flex items-center space-x-2 group">
                      <span>Learn More</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                    </button>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section id="values" className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              The foundation of everything we do at Soccer Sphere.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-slate-800 border border-slate-700 rounded-xl p-6 hover:border-green-600 transition-all hover:transform hover:scale-105">
                  <div className="bg-gradient-to-br from-green-700 to-green-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{value.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              What Our Community Says
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Real stories from players and parents who are part of the Soccer Sphere family.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-8 hover:border-green-600 transition-all hover:shadow-lg">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-green-600 text-green-600" />
                  ))}
                </div>
                <p className="text-slate-700 leading-relaxed mb-6 italic">"{testimonial.content}"</p>
                <div>
                  <div className="font-semibold text-slate-900">{testimonial.name}</div>
                  <div className="text-sm text-slate-600">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400">
              Haven't found what you're looking for? <a href="#contact" className="text-green-600 hover:text-green-500">Contact us</a>.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-700 transition"
                >
                  <span className="text-lg font-semibold text-white">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 text-gray-400 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Get In Touch
            </h2>
            <p className="text-xl text-slate-600">
              Have questions? Send us a message and we'll get back to you shortly.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
            {formSubmitted ? (
              <div className="text-center py-12">
                <div className="bg-green-600/20 border border-green-600/30 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Message Sent!</h3>
                <p className="text-slate-600">We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-slate-900 focus:outline-none focus:border-green-600 transition"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-slate-900 focus:outline-none focus:border-green-600 transition"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Program Interest</label>
                  <select
                    value={formData.program}
                    onChange={(e) => setFormData({...formData, program: e.target.value})}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-slate-900 focus:outline-none focus:border-green-600 transition"
                  >
                    <option value="">Select a program</option>
                    <option value="recreational">Recreational Division (Ages 5-7)</option>
                    <option value="precomp">Pre-Comp Division (Ages 8-11)</option>
                    <option value="competitive">Competitive Division (Ages 12-15)</option>
                    <option value="elite">Elite Division (Ages 16-18)</option>
                    <option value="general">General Questions</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                  <textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={5}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-slate-900 focus:outline-none focus:border-green-600 transition resize-none"
                    placeholder="Tell us about your soccer journey..."
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-green-700 to-green-600 text-white py-4 rounded-lg font-semibold hover:opacity-90 transition flex items-center justify-center space-x-2"
                >
                  <span>Send Message</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-r from-blue-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1622659097509-4d56de14539e?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Start Your Soccer Journey. Today.
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Join hundreds of players who are building skills, character, and community with Soccer Sphere.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#contact" className="bg-gradient-to-r from-green-700 to-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:opacity-90 transition">
              Get Started Today
            </a>
            <a href="#programs" className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/20 transition">
              View All Programs
            </a>
          </div>
          <p className="mt-8 text-sm text-gray-300">Free assessment • No credit card required • Ages 5-18</p>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/recreational" element={<RecreationalPage />} />
        <Route path="/precomp" element={<PreCompPage />} />
        <Route path="/competitive" element={<CompetitivePage />} />
        <Route path="/elite" element={<ElitePage />} />
        <Route path="/after-school" element={<AfterSchoolPage />} />
        <Route path="/contact" element={<ContactPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;